import * as Collections from 'typescript-collections';
import { Injectable } from '@angular/core';
import { keyBy, groupBy, keys, find } from 'lodash/fp';

import { BaseService } from './base.service';
import { ApiService } from './api.service';
import {
  PropertyMaster,
  StaffBasicInfo,
  PropertyMainMastersHelper,
  PropertyBasicInfo,
  GenericMaster,
  SkillLookup,
  LocationLookup,
  JobCategoryLookup,
  JobDescriptionLookup,
  LanguageLookup,
  SectionLookup,
} from '@core/models/global-entities/global-entities';
import { AssetApprovalTypes, ScheduleTypes } from '@models/pm/enum-pm';
import { TasklistService } from './tasklist.service';
import { MasterDataService } from './master-data.service';
import {
  EntityCodes,
  LocationTypes,
  ObjectStatuses,
  ReasonType,
} from '@core/models/global-entities/global-enums';
import { SimpleResponse } from '@core/models/response-models/response-models';
import { AppFeatures, AppSettings } from '@core/library/app-settings';
import { TaskGroupUIModel } from '@core/models/tasklist/tasklist';
import {
  AssetGlobalApprovalTypes,
  AssetApprovalStatuses,
} from '@core/models/assets/enum-assets';

import {
  MetricTypes,
  MetricWarningOrders,
} from '@core/models/metric/enum-metric';
import { AssetLookupRequest } from '@core/models/assets/asset';
import {
  AssetCategoryLookup,
  KMTMaster,
  KMTMasterResponse,
  KMTMastersExtDTO,
  KMTMastersExtDTOResponse,
  PMType,
  Reason,
  WODescription,
} from '@core/models/main-master/main-master';
import { Constants } from '@core/utils/constants';
import { Metric } from '@core/models/metric/metric';
import { UserGroup } from '@core/models/user/user';
import {
  WorkOrderSource,
  WorkOrderStatuses,
} from '@core/models/workorder/enum-workorder';
import { DateTime, Duration, FixedOffsetZone } from 'luxon';
import { OccupancyStatus } from '@core/models/pms/pms-enums';
import { ExcelImportEntity } from '@core/models/bulk-import/bulk-import';
import { uniqBy } from 'lodash';
export type StringKeyDictionary = Collections.Dictionary<string, any>;

@Injectable({
  providedIn: 'root',
})
export class CacheManagementService extends BaseService {
  // KSV Masters Property-wise Cache
  public Cache_PropertyMaster = new Collections.Dictionary<
    number,
    PropertyMaster
  >();
  // KMT Masters Property-wise Cache
  public Cache_KMTMasters = new Collections.Dictionary<
    string,
    StringKeyDictionary
  >();
  // KMT Masters Property independent cache (currently this only stores MeasurementUnits)
  public Cache_GlobalMasters = new Collections.Dictionary<string, any>();

  public ActivePropertyID: number;
  public ActiveLanguageCode: string;
  public LoggedInUser: StaffBasicInfo;
  public ActiveChainId: number;
  public userLocale = 'ENG';

  constructor(
    public apiService: ApiService,
    public masterService: MasterDataService,
    public tasklistService: TasklistService
  ) {
    super();
  }

  initializeConfig(): Promise<any> {
    return this.setConfig();
  }

  initializeUser(): Promise<any> {
    return this.masterService.setCurrentLoggedInUser().then((resp) => {
      if (!resp) {
        return Promise.reject(resp);
      }
      this.LoggedInUser = resp;
      this.ActivePropertyID = resp.PropertyId;
      this.ActiveLanguageCode = resp.LanguageCode;
      // this.apiService.languageCode = this.ActiveLanguageCode || 'ENG';
      this.ActiveChainId = resp.ChainId;
      return resp;
    });
  }

  initializeCache(): Promise<any> {
    return Promise.all([
      this.masterService.getAllPropertyMaster(this.ActiveLanguageCode, null),
      // this.getAllCacheFromServer(),
      this.getExtendedCacheFromServer(this.ActiveLanguageCode),
    ]).then((data) => {
      const [propertyMasterResp, mainMasterResp] = data;
      if (!propertyMasterResp || !mainMasterResp) {
        return Promise.reject(data);
      }
      if (propertyMasterResp.HasError || mainMasterResp.HasError) {
        return Promise.reject(data);
      }

      (propertyMasterResp || []).forEach((row) => {
        this.Cache_PropertyMaster.setValue(row.PropertyID, row);
      });
      const propertyMasterHelper: PropertyMainMastersHelper =
        new PropertyMainMastersHelper();
      propertyMasterHelper.TransformToLookup(this.Cache_PropertyMaster);
      this.setExtendedCacheFromServer(mainMasterResp.Result);
      // this.setAllCacheFromServer(mainMasterResp.Result);

      return Promise.resolve(data);
    });
  }

  setConfig(): Promise<any> {
    return this.apiService
      .getAPI('./config/', 'runtime.settings.json')
      .then((data) => {
        const { URL_Settings, Features, Debug } = data;
        AppSettings.URL_Settings = URL_Settings;
        AppFeatures.Debug = Debug;
        AppFeatures.SetLanguage = Features?.feature_set_language;
        AppFeatures.ChecklistRadio = Features?.feature_enable_checklist_radio;
        AppFeatures.KMTPreArrivalDaysLimit =
          Features?.feature_pre_arrival_limit;
      })
      .then(() => this.apiService.getAPI(AppSettings.URL_Settings, 'Settings'))
      .then((data) => {
        AppSettings.ShowICPNumber = data.ShowICPNumber;

        AppSettings.URL_Masters = data.URL_Masters;
        AppSettings.URL_Users = data.URL_Users;
        AppSettings.URL_PMSService = data.URL_PMSService;
        AppSettings.URL_Session = data.URL_Session;
        AppSettings.URL_Central = data.URL_Central;
        AppSettings.URL_ComplainService = data.URL_ComplainService;

        AppSettings.URL_Asset = data.URL_Asset;
        AppSettings.URL_MNT_BulkAction = data.URL_MNT_BulkAction;
        AppSettings.URL_MNT_Gateway = data.URL_MNT_Gateway;
        AppSettings.URL_KMTNotification = data.URL_KMTNotification;
        AppSettings.URL_Analytics = data.URL_Analytics;
        AppSettings.URL_Inventory = data.URL_Inventory;
        AppSettings.URL_KMTMastersV2 = data.URL_KMTMasters;
        AppSettings.URL_WorkOrders = data.URL_WorkOrders;
        AppSettings.URL_PlatformConfig = data.URL_PlatformConfig;
        AppSettings.URL_Checklist = data.URL_Checklist;
        AppSettings.URL_KMT = data.URL_KMT;
        AppSettings.URL_KMTReports = data.URL_KMTReports;
        AppSettings.URL_Attachments = data.URL_Attachments;
        AppSettings.URL_KMTConfig = data.URL_KMTConfig;
        AppSettings.URL_KMTImport = data.URL_KMTImport;
        AppSettings.URL_PM = data.URL_PM;
        AppSettings.Domain = data.Domain;
      });
  }

  getPropertyCache(PropertyId: number): PropertyMaster {
    return this.Cache_PropertyMaster.getValue(+PropertyId);
  }

  getKMTCache(
    propertyId: number,
    entityCode: EntityCodes
  ): StringKeyDictionary {
    const key = this.getCacheKey(propertyId, entityCode);
    return this.Cache_KMTMasters.getValue(key);
  }

  getProperties(): PropertyBasicInfo[] {
    return this.LoggedInUser.Properties;
  }

  getPropertyInfo(propertyID: number): PropertyBasicInfo[] {
    const properties = this.getProperties() || [];
    return properties.filter((p) => p.PropertyId === propertyID);
  }

  getPropertyOffsetInSec(propertyId: number): number {
    const [property] = this.getPropertyInfo(propertyId);
    return property?.BaseUtcOffset ?? 0;
  }

  getLocalOffsetInSec(): number {
    const offset = new Date().getTimezoneOffset() * 60;
    return offset;
  }

  getPropertyTimezone(propertyId: number): string {
    const [property] = this.getPropertyInfo(propertyId);
    const offset = property?.BaseUtcOffset;
    if (!offset) {
      return '00:00';
    }
    const sign = offset === 0 ? '' : offset > 0 ? '+' : '-';
    const duration = Duration.fromObject({ seconds: Math.abs(offset) });
    return `${sign}${duration.toFormat('hh:mm')}`;
  }

  GetPropertyTime(propertyId: number): DateTime {
    const offset = this.getPropertyOffsetInSec(propertyId);
    const zone = FixedOffsetZone.instance(offset / 60);
    const local = DateTime.local();
    const rezoned = local.setZone(zone);
    return rezoned;
  }

  getPropertyLocations(
    PropertyId: number,
    LocationType: LocationTypes = null
  ): any {
    const locations = this.getPropertyCache(PropertyId)?.Location ?? [];
    return LocationType
      ? locations.filter((l) => l.LocationTypeCode === LocationType)
      : locations;
  }

  getPropertySections(PropertyId: number): any {
    const sections = this.getPropertyCache(PropertyId)?.Sections ?? [];
    return sections;
  }

  getOccupancyStatusText(status: OccupancyStatus): string {
    if (!status) {
      return '';
    }
    switch (status) {
      case OccupancyStatus.Vacant:
        return 'LBL.VACANT';
      case OccupancyStatus.PreArrival:
        return 'MASTER.MASTER_RESERVATION_STATUS_1';
      case OccupancyStatus.InHouse:
        return 'MASTER.MASTER_RESERVATION_STATUS_2';
      case OccupancyStatus.DueOut:
        return 'LBL.DUE_OUT';
      case OccupancyStatus.DueOutPreArrival:
        return 'LBL.DUE_OUT_PRE_ARRIVAL';
    }
  }

  getOccupancyStatusClass(status: OccupancyStatus): string {
    if (!status) {
      return '';
    }
    switch (status) {
      case OccupancyStatus.Vacant:
        // return 'kmt-icon-vacant';
        return 'Vacant.png'; //1
      case OccupancyStatus.PreArrival:
        return 'PreArr.png'; // 4
      case OccupancyStatus.InHouse:
        return 'InHouse.png'; // 2
      case OccupancyStatus.DueOut:
        return 'DueOut.png'; // 3
      case OccupancyStatus.DueOutPreArrival:
        return 'DueOutPreArr.png'; // 5
    }
  }

  getPropertyJobCategories(PropertyId: number): JobCategoryLookup[] {
    return this.getPropertyCache(PropertyId)?.CallCategories ?? [];
  }

  getPropertyJobsByCategoryId(PropertyId: number, categoryId: number) {
    let jobCategories = this.getPropertyJobCategories(PropertyId);
    let job = [];
    if (jobCategories && jobCategories.length) {
      job = jobCategories.filter((elem) => elem.Id == categoryId);
    }
    return job;
  }

  getJobDescriptions(
    PropertyId: number,
    showDeleted = true
  ): JobDescriptionLookup[] {
    const descriptions =
      this.getPropertyCache(PropertyId)?.CallDescriptions ?? [];
    return showDeleted
      ? descriptions
      : descriptions.filter((desc) => !desc.IsDel);
  }

  getJobDescriptionsById(PropertyId: number, id: number, showDeleted = true) {
    const descriptions = this.getJobDescriptions(PropertyId, showDeleted);
    let desById = descriptions.filter((elem) => elem.Id == id);
    return desById;
  }

  getLocations(PropertyId: number, Locations?: number[]): LocationLookup[] {
    if (!Locations) {
      return null;
    }
    const propertyLocations = this.getPropertyLocations(PropertyId);
    const locationsById = keyBy<LocationLookup>('Id')(propertyLocations);
    return Locations.map((locationId) => locationsById[locationId]);
  }

  getLocationsWithMulProp(PropertyIds: number[]): LocationLookup[] {
    let allLocations = [];

    for (let each of PropertyIds) {
      allLocations = allLocations.concat(this.getPropertyLocations(each));
    }
    return allLocations;
  }

  getSections(PropertyIds: number[] = []) {
    let sections = [];
    for (let each of PropertyIds) {
      sections = sections.concat(this.getPropertySections(each));
    }
    return sections;
  }

  getSection(sectionId: any, PropertyIds: number[]) {
    let sections: Array<SectionLookup> = [];
    for (let each of PropertyIds) {
      sections = sections.concat(this.getPropertySections(each));
    }
    let foundSection = sections.find((s) => s.Id == sectionId);
    return foundSection;
  }

  getLocationById(PropertyId: number, LocationById: number) {
    const propertyLocations = this.getPropertyLocations(PropertyId);
    const locationsById = keyBy<LocationLookup>('Id')(propertyLocations);
    return locationsById[LocationById];
  }
  getStaffList(PropertyId: number): Array<GenericMaster> {
    return this.getPropertyCache(PropertyId)?.Staff ?? [];
  }

  getStaff(PropertyId: number, StaffId: number): GenericMaster {
    const staffList = this.getStaffList(PropertyId) || [];
    const staffListById = keyBy<GenericMaster>('Id')(staffList);
    return staffListById[StaffId];
  }

  getSkillsByDepartment(
    PropertyId: number,
    DepartmentId?: number[]
  ): SkillLookup[] {
    if (!PropertyId) {
      return [];
    }
    const skills = this.getPropertyCache(PropertyId).Skill || [];
    if (!DepartmentId?.length) {
      return skills;
    }
    return skills.filter((skill) => DepartmentId.indexOf(skill.DeptId) >= 0);
  }

  getSkills(PropertyId: number, SkillId?: number[]): Array<SkillLookup> {
    const skills = this.getPropertyCache(PropertyId).Skill || [];
    if (SkillId) {
      const skillsById = keyBy((skill: SkillLookup) => skill.Id)(skills);
      return SkillId.reduce((acc, skillId) => {
        const skill = skillsById[skillId];
        if (skill) {
          acc.push(skill);
        }
        return acc;
      }, []);
    } else {
      return skills;
    }
  }

  getDepartments(PropertyId: number) {
    return this.getPropertyCache(PropertyId)?.Dept;
  }
  getDepartmentById(PropertyId: number, deptId: number) {
    const depts = this.getPropertyCache(PropertyId).Dept || [];
    const deptsById = keyBy((dept: GenericMaster) => dept.Id)(depts);
    const dept = deptsById[deptId];
    return dept;
  }

  getLanguage(PropertyId: number): LanguageLookup[] {
    return this.getPropertyCache(PropertyId)?.Language ?? [];
  }

  getComplainStatus(PropertyId: number): GenericMaster[] {
    return this.getPropertyCache(PropertyId)?.ComplainStatus ?? [];
  }

  getOtherLanguages(PropertyId: number): LanguageLookup[] {
    return this.getLanguage(PropertyId).filter(
      (lang) => lang.LanguageCode !== 'ENG'
    );
  }

  getDesignations(PropertyId: number) {
    return this.getPropertyCache(PropertyId)?.Desg;
  }

  getReasons(PropertyId: number, type: ReasonType = null): Reason[] {
    const reasons: Reason[] =
      this.getCache(PropertyId, EntityCodes.Reasons) || [];

    return type
      ? reasons.filter((reason) => reason.ReasonType === type)
      : reasons;
  }

  getReasonById(
    PropertyId: number,
    type: ReasonType,
    reasonId: number
  ): Reason {
    const reasons = this.getReasons(PropertyId, type);
    return find((r: Reason) => r.Id === reasonId)(reasons);
  }

  getAssetGlobalApprovalList() {
    return [
      {
        Id: AssetGlobalApprovalTypes.NoApproval,
        Name: Constants.UILabels.NoApproval,
      },
      {
        Id: AssetGlobalApprovalTypes.UserGroup,
        Name: Constants.UILabels.UserGroup,
      },
      {
        Id: AssetGlobalApprovalTypes.FrontOffice,
        Name: Constants.UILabels.FrontOffice,
      },
    ];
  }

  getAssetApprovalTypes() {
    return [
      {
        Id: AssetApprovalTypes.OverrideApproval,
        Name: Constants.UILabels.OverrideAssetSetting,
      },
      {
        Id: AssetApprovalTypes.UseAssetSetting,
        Name: Constants.UILabels.UseAssetSetting,
      },
    ];
  }

  getScheduleTypes(): GenericMaster<ScheduleTypes>[] {
    return [
      // {
      //   Id: ScheduleTypes.NoSchedule,
      //   Name: Constants.UILabels.None,
      // },
      {
        Id: ScheduleTypes.Recurring,
        Name: Constants.UILabels.Pattern,
      },
      // {
      //   Id: ScheduleTypes.FixedDates,
      //   Name: Constants.UILabels.FixedDates,
      // },
      {
        Id: ScheduleTypes.Gap,
        Name: Constants.UILabels.Gap,
      },
    ];
  }

  getObjectStatuses() {
    return [
      {
        code: ObjectStatuses.Active,
        label: Constants.UILabels.Active,
      },
      {
        code: ObjectStatuses.Deleted,
        label: Constants.UILabels.Deleted,
      },
      {
        code: ObjectStatuses.Disabled,
        label: Constants.UILabels.Disabled,
      },
    ];
  }

  getApprovalStatuses(approvalStatus: AssetApprovalStatuses) {
    let statusString = Constants.UILabels.NotApproved;
    if (!approvalStatus) {
      return statusString;
    }
    switch (approvalStatus) {
      case AssetApprovalStatuses.Approved:
        statusString = Constants.UILabels.Approved;
        break;
      case AssetApprovalStatuses.NotApproved:
        statusString = Constants.UILabels.Rejected;
        break;
    }
    return statusString;
  }

  getMetricTypes() {
    return [
      {
        Id: MetricTypes.Maintenance,
        Name: Constants.UILabels.Maintenance,
      },
      {
        Id: MetricTypes.Checklist,
        Name: Constants.UILabels.Checklist,
      },
      {
        Id: MetricTypes.MeterBased,
        Name: Constants.UILabels.MeterBased,
      },
      {
        Id: MetricTypes.Audit,
        Name: Constants.UILabels.Audit,
      },
    ];
  }

  getWarningOrders() {
    return [
      {
        Id: MetricWarningOrders.CheckUp,
        Name: Constants.UILabels.CheckUp,
      },
      {
        Id: MetricWarningOrders.CheckDown,
        Name: Constants.UILabels.CheckDown,
      },
    ];
  }

  getObjectStatus(status: ObjectStatuses) {
    return this.getObjectStatuses().filter((o) => o.code === status);
  }

  getWorkOrderSources(): GenericMaster<WorkOrderSource>[] {
    return [
      {
        Id: WorkOrderSource.Staff,
        Name: 'LBL.STAFF',
      },
      {
        Id: WorkOrderSource.Guest,
        Name: 'LBL.GUEST',
      },
      {
        Id: WorkOrderSource.PM,
        Name: 'LBL.PM',
      },
    ];
  }

  getWorkOrderStatuses(
    showCancelled = false
  ): GenericMaster<WorkOrderStatuses>[] {
    return [
      {
        Name: 'LBL.UNASSIGNED',
        Id: WorkOrderStatuses.Created,
      },
      {
        Name: 'LBL.ASSIGNED',
        Id: WorkOrderStatuses.Assigned,
      },
      {
        Name: 'LBL.LBL_PENDING_APPROVE',
        Id: WorkOrderStatuses.PendingApproval,
      },
      {
        Name: 'LBL.IN_PROGRESS',
        Id: WorkOrderStatuses.Started,
      },
      {
        Name: 'LBL.LBL_PARKED',
        Id: WorkOrderStatuses.Parked,
      },
      // {
      //   Name: 'Pending Inspection',
      //   Id: WorkOrderStatuses.AwaitingAudit,
      // },
      {
        Name: 'LBL.LBL_COMPLETED',
        Id: WorkOrderStatuses.Closed,
      },
      ...(showCancelled
        ? [
            {
              Name: 'LBL.LBL_CANCELLED',
              Id: WorkOrderStatuses.Cancelled,
            },
          ]
        : []),
    ];
  }

  MasterData_GetMutli(
    PropertyID: number,
    ID: any,
    EntityCode: EntityCodes,
    CurrentValue: any
  ): any {
    if (ID == null) {
      return '';
    }
    const obj = new PropertyMainMastersHelper();

    return obj.GetMasterValue(
      this.Cache_PropertyMaster,
      PropertyID,
      ID,
      EntityCode,
      CurrentValue
    );
  }

  MasterData_GetMutli_MultiProp(
    PropertyID: number[],
    ID: any,
    EntityCode: EntityCodes,
    CurrentValue: any
  ): any {
    if (ID == null) {
      return '';
    }
    const obj = new PropertyMainMastersHelper();
    let toReturn;
    for (let each of PropertyID) {
      if (!toReturn) {
        toReturn = obj.GetMasterValue(
          this.Cache_PropertyMaster,
          each,
          ID,
          EntityCode,
          CurrentValue
        );
      } else break;
    }
    return toReturn;
  }

  getCacheKey(
    PropertyId: number,
    EntityCode: EntityCodes,
    EntityId?: string
  ): string {
    const key = PropertyId + '_' + EntityCode;
    if (EntityId && EntityId !== '') {
      return key + '_' + EntityId;
    }
    return key;
  }

  setCacheFromServer(
    PropertyId: number,
    EntityCode: EntityCodes
  ): Promise<any> {
    return this.getCacheFromServer(PropertyId, EntityCode).then((data) => {
      if (data && !data.HasError) {
        this.setCacheEntities(PropertyId, EntityCode, data.Result);
      }
      return data;
    });
  }

  setCache(
    PropertyId: number,
    EntityCode: EntityCodes,
    EntityId: any,
    Entity: any
  ): any {
    const cacheKey: string = this.getCacheKey(PropertyId, EntityCode);
    const entityCache =
      this.Cache_KMTMasters.getValue(cacheKey) ||
      new Collections.Dictionary<string, any>();
    const entityCacheKey = this.getCacheKey(PropertyId, EntityCode, EntityId);
    entityCache.setValue(entityCacheKey, Entity);
  }

  setGlobalCache(EntityCode: EntityCodes, entity: any) {
    this.Cache_GlobalMasters.setValue(EntityCode, entity);
  }

  getGlobalCache(EntityCode: EntityCodes): any {
    return this.Cache_GlobalMasters.getValue(EntityCode) || null;
  }

  setCacheEntities(
    PropertyId: number,
    EntityCode: EntityCodes,
    entities: Array<any>
  ) {
    const cacheKey = this.getCacheKey(PropertyId, EntityCode);
    const entityCache =
      this.Cache_KMTMasters.getValue(cacheKey) ||
      new Collections.Dictionary<string, any>();

    const entityToIndexField = {
      [EntityCodes.AssetCategory]: 'AssetCategoryId',
      [EntityCodes.PMType]: 'PMTypeId',
      [EntityCodes.Metric]: 'MetricId',
      [EntityCodes.TaskGroup]: 'TaskGroupId',
      [EntityCodes.UserGroup]: 'UserGroupId',
      [EntityCodes.Task]: 'TaskId',
      [EntityCodes.WODescription]: 'Id',
      [EntityCodes.WOTypes]: 'Id',
      [EntityCodes.Currency]: 'Id',
    };

    const indexField = entityToIndexField[EntityCode];
    if (!indexField) {
      return;
    }
    entities.forEach((entity) => {
      const entityId = entity[indexField];
      if (entityId) {
        const entityCacheKey = this.getCacheKey(
          PropertyId,
          EntityCode,
          entityId
        );
        entityCache.setValue(entityCacheKey, entity);
      }
    });

    this.Cache_KMTMasters.setValue(cacheKey, entityCache);
  }

  setInPropertyCache(entity: EntityCodes) {
    return (groupedEntities) =>
      keys(groupedEntities).forEach((propertyId) =>
        this.setCacheEntities(
          parseInt(propertyId, 10),
          entity,
          groupedEntities[propertyId]
        )
      );
  }

  getCache(
    PropertyId: number,
    EntityCode: EntityCodes,
    EntityId?: string
  ): any {
    // global cache entities
    if (
      EntityCode === EntityCodes.MeasurementUnit ||
      EntityCode === EntityCodes.Reasons ||
      EntityCode === EntityCodes.WhitelistDept
    ) {
      return this.getGlobalCache(EntityCode);
    }
    // get dictionary from kmt cache
    const cache = this.getKMTCache(PropertyId, EntityCode);
    // if no cache, then return early
    if (!cache) {
      return null;
    }
    // if no entity id specified, return all values
    if (!EntityId) {
      return cache.values().filter((v) => !v['IsDel']);
    }
    // generate full entity-id based key
    const key: string = this.getCacheKey(PropertyId, EntityCode, EntityId);
    // use key to fetch any cached value
    if (cache.containsKey(key)) {
      return cache.getValue(key);
    }

    return null;
  }

  getCacheFromServer(PropertyId: number, EntityCode: string): Promise<any> {
    switch (EntityCode) {
      case EntityCodes.Task:
        return this.tasklistService.Tasks_All(PropertyId);
      case EntityCodes.TaskGroup:
        return this.tasklistService.TaskGroup_All(PropertyId);
      case EntityCodes.AssetCategory:
        return this.masterService.getAssetCategories(PropertyId);
      case EntityCodes.PMType:
        return this.masterService.getPMTypes(PropertyId);
      case EntityCodes.UserGroup:
        return this.masterService.UserGroup_All(PropertyId);
      case EntityCodes.Metric:
        return this.masterService.Metrics_All(PropertyId);
    }
  }

  getAllCacheFromServer(): Promise<KMTMasterResponse> {
    return this.masterService.getMainMasters();
  }

  getExtendedCacheFromServer(
    LanguageCode: string
  ): Promise<KMTMastersExtDTOResponse> {
    LanguageCode = LanguageCode || 'ENG';
    return this.masterService.getExtendedMasters(LanguageCode);
  }

  setAllCacheFromServer(
    AllMaster: KMTMaster,
    CacheMaster = this.Cache_KMTMasters
  ) {
    if (!CacheMaster) {
      return;
    }

    const AssetCategoriesByProperty = groupBy<AssetCategoryLookup>(
      'PropertyId'
    )(AllMaster.AssetCategories || []);
    // TODO: add levels, & path to asset category flat list
    keys(AssetCategoriesByProperty).forEach((propertyId) => {
      const assetCategories = this.masterService.generateAssetCatTree(
        AssetCategoriesByProperty[propertyId]
      );
      this.setCacheEntities(
        +propertyId,
        EntityCodes.AssetCategory,
        assetCategories
      );
    });
    const PMTypesByProperty = groupBy<PMType>('PropertyId')(
      AllMaster.PMTypes || []
    );
    keys(PMTypesByProperty).forEach((propertyId) => {
      this.setCacheEntities(
        +propertyId,
        EntityCodes.PMType,
        PMTypesByProperty[propertyId]
      );
    });

    this.setGlobalCache(
      EntityCodes.MeasurementUnit,
      AllMaster.MeasurementUnits || []
    );
    const MetricsByProperty = groupBy<Metric>('PropertyId')(
      AllMaster.Metrics || []
    );
    keys(MetricsByProperty).forEach((propertyId) => {
      this.setCacheEntities(
        +propertyId,
        EntityCodes.Metric,
        MetricsByProperty[propertyId]
      );
    });
    const TaskGroupsByProperty = groupBy('PropertyId')(
      AllMaster.TaskGroups || []
    );
    keys(TaskGroupsByProperty).forEach((propertyId) => {
      this.setCacheEntities(
        +propertyId,
        EntityCodes.TaskGroup,
        TaskGroupsByProperty[propertyId]
      );
    });
    const UserGroupsByProperty = groupBy<UserGroup>('PropertyId')(
      AllMaster.UserGroups || []
    );
    keys(UserGroupsByProperty).forEach((propertyId) => {
      this.setCacheEntities(
        +propertyId,
        EntityCodes.UserGroup,
        UserGroupsByProperty[propertyId]
      );
    });
    return CacheMaster;
  }

  setExtendedCacheFromServer(ExtendedMaster: KMTMastersExtDTO) {
    ExtendedMaster = ExtendedMaster || new KMTMastersExtDTO();
    const Descriptions = groupBy<WODescription>(
      'PropertyId',
      ExtendedMaster.Descriptions || []
    );
    const WorkOrderTypes = groupBy(
      'PropertyId',
      ExtendedMaster.WorkOrderTypes || []
    );
    const Currencies = groupBy('PropertyId', ExtendedMaster.Currency || []);
    const Reasons = ExtendedMaster.Reasons || [];
    const WhitelistDept = ExtendedMaster.DepartmentWhitelists || [];

    this.setInPropertyCache(EntityCodes.WODescription)(Descriptions);
    this.setInPropertyCache(EntityCodes.WOTypes)(WorkOrderTypes);
    this.setInPropertyCache(EntityCodes.Currency)(Currencies);
    this.setGlobalCache(EntityCodes.Reasons, Reasons);
    this.setGlobalCache(EntityCodes.WhitelistDept, WhitelistDept);
  }

  assetLookup(requestObj: AssetLookupRequest): Promise<SimpleResponse> {
    return this.apiService
      .postAPI(AppSettings.URL_KMTMasters, 'Asset/Asset_Lookup_Get', requestObj)
      .then((data) => {
        return data;
      });
  }

  format_Task_Group(PropertyId: number, Tasks) {
    const TaskGroups: Array<TaskGroupUIModel> =
      this.getCache(PropertyId, EntityCodes.TaskGroup) || [];

    const Task_GroupBy = groupBy<any>('TaskGroupId')(Tasks);
    TaskGroups.forEach((g) => {
      g.Tasks = Task_GroupBy[g.TaskGroupId] || [];
    });
    this.setCacheEntities(PropertyId, EntityCodes.TaskGroup, TaskGroups);
  }

  getTask(PropertyId: number, TaskId: number) {
    const PropertyTasks = this.getCache(PropertyId, EntityCodes.Task) || [];
    const TasksById = keyBy('TaskId')(PropertyTasks);
    return TasksById[TaskId];
  }

  getCallDescCache(PropertyId: number, EntityId?: string) {
    const calDescs = this.getJobDescriptions(PropertyId);

    if (!calDescs) {
      return;
    }
    const kmtDescs = calDescs.filter((j) => j.Id == Number(EntityId));
    if (!kmtDescs) {
      return;
    }

    return kmtDescs[0];
  }

  getExcelImportEntities(): GenericMaster<ExcelImportEntity>[] {
    return [
      {
        Id: ExcelImportEntity.Asset,
        Name: 'LBL.ASSET',
      },
      {
        Id: ExcelImportEntity.PM,
        Name: 'LBL.PMS',
      },
      {
        Id: ExcelImportEntity.Checklist,
        Name: 'LBL.CHECKLISTS',
      },
      {
        Id: ExcelImportEntity.Contractor,
        Name: 'LBL.LBL_CONTRACTOR',
      },
      {
        Id: ExcelImportEntity.WODescription,
        Name: 'LBL.LBL_WO_DESCRIPTION',
      },
    ];
  }
}
